<template>
  <Cursor />
  <div>
    <transition name="fade">
      <div v-if="isLoading" class="preloader">
        <div class="loader-content">
          <!-- Background Elements -->
          <div class="background-elements">
            <div class="gradient-sphere"></div>
            <div class="grid-overlay"></div>
            <div class="floating-elements">
              <div class="tech-circle"></div>
              <div class="tech-square"></div>
              <div class="tech-dots"></div>
            </div>
          </div>

          <!-- Logo Animation -->
          <div class="logo-container">
            <div
              class="logo-wrapper animate-fade"
              :class="{ visible: startAnimation }"
            >
              <span class="logo-text">Vision Lab</span>
              <span class="logo-dot"></span>
            </div>

            <!-- Loading Progress -->
            <div class="loading-bar-container">
              <div
                class="loading-bar"
                :style="{ width: loadingProgress + '%' }"
              ></div>
            </div>

            <!-- Loading Text -->
            <div class="loading-text" :class="{ visible: startAnimation }">
              {{ loadingText }}
            </div>
          </div>
        </div>
      </div>
    </transition>

    <div v-if="!isLoading">
      <Header />
      <router-view />
      <Footer />
    </div>
  </div>
</template>

<script>
import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import Cursor from "@/components/Cursor.vue";

export default {
  name: "App",
  components: {
    Header,
    Footer,
    Cursor,
  },
  data() {
    return {
      isLoading: true,
      startAnimation: false,
      loadingProgress: 0,
      loadingText: "Initializing...",
      loadingTexts: [
        "Initializing...",
        "Loading assets...",
        "Preparing interface...",
        "Almost ready...",
      ],
    };
  },
  methods: {
    animateLoading() {
      let progress = 0;
      let textIndex = 0;

      const interval = setInterval(() => {
        if (progress >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            this.isLoading = false;
          }, 500);
          return;
        }

        progress += 1;
        this.loadingProgress = progress;

        if (progress === 25) {
          textIndex = 1;
        } else if (progress === 50) {
          textIndex = 2;
        } else if (progress === 75) {
          textIndex = 3;
        }

        this.loadingText = this.loadingTexts[textIndex];
      }, 20);
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    scrollToHash(hash) {
      this.$nextTick(() => {
        setTimeout(() => {
          const element = document.querySelector(hash);
          if (element) {
            element.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 100);
      });
    },
  },
  mounted() {
    setTimeout(() => {
      this.startAnimation = true;
      this.animateLoading();
    }, 100);
  },
  watch: {
    $route(to, from) {
      // Check if route actually changed (not just hash)
      if (to.path !== from.path) {
        this.scrollToTop();
      } else if (to.hash && to.path === "/") {
        // If only hash changed on home page, scroll to section
        this.scrollToHash(to.hash);
      }
    },
  },
};
</script>

<style lang="scss">
body {
  margin: 0;
  padding: 0;
  font-family: "Inter", system-ui, sans-serif;
  background: #000000;
}

.preloader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: #000000;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loader-content {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.background-elements {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.gradient-sphere {
  position: absolute;
  top: -20%;
  right: -10%;
  width: 80vh;
  height: 80vh;
  background: radial-gradient(
    circle,
    rgba(61, 115, 255, 0.1) 0%,
    rgba(37, 99, 235, 0.05) 40%,
    transparent 70%
  );
  border-radius: 50%;
  animation: spherePulse 4s ease-in-out infinite;
}

.grid-overlay {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(#3d73ff08 1px, transparent 1px),
    linear-gradient(90deg, #3d73ff08 1px, transparent 1px);
  background-size: 40px 40px;
  mask-image: radial-gradient(circle at 50% 50%, black, transparent 80%);
  opacity: 0;
  animation: gridFade 1s ease forwards 0.3s;
}

.floating-elements {
  position: absolute;
  inset: 0;

  .tech-circle {
    position: absolute;
    top: 20%;
    right: 15%;
    width: 200px;
    height: 200px;
    border: 2px solid rgba(61, 115, 255, 0.1);
    border-radius: 50%;
    opacity: 0;
    animation: floatElement 3s ease infinite, fadeIn 0.5s ease forwards 0.6s;
  }

  .tech-square {
    position: absolute;
    bottom: 25%;
    left: 10%;
    width: 150px;
    height: 150px;
    border: 2px solid rgba(61, 115, 255, 0.1);
    transform: rotate(45deg);
    opacity: 0;
    animation: floatElement 3s ease infinite 1s, fadeIn 0.5s ease forwards 0.8s;
  }

  .tech-dots {
    position: absolute;
    top: 30%;
    left: 20%;
    width: 100px;
    height: 100px;
    background-image: radial-gradient(
      circle,
      rgba(61, 115, 255, 0.3) 1px,
      transparent 1px
    );
    background-size: 10px 10px;
    opacity: 0;
    animation: floatElement 3s ease infinite 0.5s, fadeIn 0.5s ease forwards 1s;
  }
}

.logo-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  padding: 20px;
  border-radius: 8px;
}

.logo-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s cubic-bezier(0.22, 1, 0.36, 1);
}

.logo-wrapper.visible {
  opacity: 1;
  transform: translateY(0);
}

.logo-text {
  font-size: 48px;
  font-weight: 700;
  color: white;
}

.logo-dot {
  width: 8px;
  height: 8px;
  background: #3d73ff;
  border-radius: 50%;
  margin-left: 2px;
  animation: pulseDot 2s infinite;
}

.loading-bar-container {
  width: 200px;
  height: 2px;
  border-radius: 4px;
  overflow: hidden;
}

.loading-bar {
  height: 100%;
  background: linear-gradient(90deg, #3d73ff 0%, #2b5bdf 100%);
  transition: width 0.3s ease-out;
}

.loading-text {
  font-size: 14px;
  color: white;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.6s cubic-bezier(0.22, 1, 0.36, 1);
}

.loading-text.visible {
  opacity: 1;
  transform: translateY(0);
}

@keyframes pulseDot {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.5);
    opacity: 0.5;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes spherePulse {
  0% {
    transform: scale(1);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 0.5;
  }
}

@keyframes floatElement {
  0% {
    transform: translate(0, 0);
  }
  50% {
    transform: translate(10px, -10px);
  }
  100% {
    transform: translate(0, 0);
  }
}

@keyframes gridFade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.fade-enter-active {
  transition: opacity 0.3s ease;
}

.fade-leave-active {
  transition: all 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .logo-text {
    font-size: 36px;
  }

  .loading-bar-container {
    width: 160px;
  }

  .floating-elements {
    .tech-circle,
    .tech-square {
      display: none;
    }
  }
}
</style>
